import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const AppContext = createContext();

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'API error');
  return data;
};

// ── Normalizers ───────────────────────────────────────────────────────────────
const normalizeAsset = (a) => ({
  id:            a.asset_id,
  serial:        a.serial,
  brand:         a.brand,
  model:         a.model,
  config:        a.config,
  processor:     a.processor,
  ram:           a.ram,
  storage:       a.storage,
  purchaseDate:  a.purchase_date?.split('T')[0],
  warrantyStart: a.warranty_start?.split('T')[0],
  warrantyEnd:   a.warranty_end?.split('T')[0],
  vendor:        a.vendor,
  location:      a.location,
  notes:         a.notes,
  status:        a.status,
});

const normalizeAllocation = (a) => ({
  id:              a.allocation_id,
  dbId:            a.id,
  empId:           a.emp_id,
  empName:         a.emp_name,
  empEmail:        a.emp_email,
  department:      a.department,
  client:          a.client,
  project:         a.project,
  assetId:         a.asset_id,
  allocationDate:  a.allocation_date?.split('T')[0],
  returnDate:      a.return_date?.split('T')[0],
  accessories:     a.accessories || [],
  status:          a.status,
  brand:           a.brand,
  model:           a.model,
  config:          a.config,
  mobileNo:        a.mobile_no        || '',
  personalEmail:   a.personal_email   || '',
  photoUrl:        a.photo_url        || '',
  deliveryMethod:  a.delivery_method  || 'hand',
  deliveryAddress: a.delivery_address || '',
  notes:           a.notes            || '',
  preparedBy:      a.prepared_by      || '',
  allocatedBy:     a.allocated_by     || a.prepared_by || '',
  damagePhotos:    a.damage_photos    || '[]',
});

const normalizeRepair = (r) => ({
  id:              r.repair_id,
  dbId:            r.id,
  assetId:         r.asset_id,
  serial:          r.serial,
  issue:           r.issue,
  vendor:          r.vendor,
  repairDate:      r.repair_date?.split('T')[0],
  estimatedReturn: r.estimated_return?.split('T')[0],
  status:          r.status,
  notes:           r.notes,
  brand:           r.brand,
  model:           r.model,
});

const normalizeScrap = (s) => ({
  id:         s.scrap_id,
  dbId:       s.id,
  assetId:    s.asset_id,
  serial:     s.serial,
  model:      s.model,
  scrapDate:  s.scrap_date?.split('T')[0],
  reason:     s.reason,
  approvedBy: s.approved_by,
  brand:      s.brand,
});

// ── Provider ──────────────────────────────────────────────────────────────────
export function AppProvider({ children }) {
  const { user } = useAuth();
  const isEmployee = user?.role === 'employee';

  const [assets,      setAssets]      = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [repairs,     setRepairs]     = useState([]);
  const [scraps,      setScraps]      = useState([]);
  const [users,       setUsers]       = useState([]);
  const [stats,       setStats]       = useState({ total:0, stock:0, allocated:0, repair:0, scrap:0 });
  const [loading,     setLoading]     = useState(true);
  const [toasts,      setToasts]      = useState([]);

  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);

  const fetchAll = useCallback(async () => {
    // Employees have no access to these admin-only endpoints — skip entirely.
    if (isEmployee) { setLoading(false); return; }

    try {
      setLoading(true);
      const [assetsRes, allocsRes, repairsRes, scrapsRes, statsRes, usersRes] = await Promise.all([
        apiFetch('/assets?limit=10000'),
        apiFetch('/allocations?limit=10000'),
        apiFetch('/repairs'),
        apiFetch('/scraps'),
        apiFetch('/assets/stats'),
        apiFetch('/users'),
      ]);
      setAssets(assetsRes.data.map(normalizeAsset));
      setAllocations(allocsRes.data.map(normalizeAllocation));
      setRepairs(repairsRes.data.map(normalizeRepair));
      setScraps(scrapsRes.data.map(normalizeScrap));
      setUsers(usersRes.data || []);
      const s = statsRes.data;
      setStats({
        total:     Number(s.total),
        stock:     Number(s.stock),
        allocated: Number(s.allocated),
        repair:    Number(s.repair),
        scrap:     Number(s.scrap),
      });
    } catch (err) {
      showToast(err.message || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, isEmployee]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Add Asset ─────────────────────────────────────────────────────────────
  const addAsset = async (formData) => {
    try {
      await apiFetch('/assets', {
        method: 'POST',
        body: JSON.stringify({
          asset_id:       formData.id,
          serial:         formData.serial,
          brand:          formData.brand,
          model:          formData.model,
          config:         formData.config,
          processor:      formData.processor,
          ram:            formData.ram,
          storage:        formData.storage,
          purchase_date:  formData.purchaseDate  || null,
          warranty_start: formData.warrantyStart || null,
          warranty_end:   formData.warrantyEnd   || null,
          vendor:         formData.vendor,
          location:       formData.location,
          notes:          formData.notes,
        }),
      });
      showToast('Asset added successfully');
      await fetchAll();
    } catch (err) {
      showToast(err.message, 'error');
      throw err;
    }
  };

  // ── Allocate Asset ────────────────────────────────────────────────────────
  const allocateAsset = async (assetId, formData) => {
    try {
      await apiFetch('/allocations', {
        method: 'POST',
        body: JSON.stringify({
          asset_id:         assetId,
          emp_id:           formData.empId,
          emp_name:         formData.empName,
          emp_email:        formData.empEmail,
          department:       formData.department,
          client:           formData.client,
          project:          formData.project,
          allocation_date:  formData.allocationDate,
          accessories:      formData.accessories      || [],
          extra_ccs:        formData.extra_ccs        || [],
          mobile_no:        formData.mobileNo         || '',
          personal_email:   formData.personalEmail    || '',
          photo_url:        formData.photoUrl         || '',
          delivery_method:  formData.deliveryMethod   || 'hand',
          delivery_address: formData.deliveryAddress  || '',
          accessoryDetails: formData.accessoryDetails || [],
          prepared_by:      formData.prepared_by || formData.preparedBy || '',
          damage_photos:    Array.isArray(formData.damage_photos)
                              ? JSON.stringify(formData.damage_photos)
                              : (formData.damage_photos || '[]'),
        }),
      });
      showToast('Laptop allocated successfully');
      await fetchAll();
    } catch (err) {
      showToast(err.message, 'error');
      throw err;
    }
  };

  // ── Receive Asset ─────────────────────────────────────────────────────────
  const receiveAsset = async (
    allocationId,
    assetId,
    condition,
    damageDesc          = '',
    extraCCs            = [],
    returnPhotos        = [],
    returnedAccessories = [],
  ) => {
    try {
      await apiFetch(`/allocations/${allocationId}/receive`, {
        method: 'PUT',
        body: JSON.stringify({
          condition,
          damage_description:   damageDesc,
          extra_ccs:            extraCCs,
          return_photos:        returnPhotos,
          returned_accessories: returnedAccessories,
        }),
      });
      showToast('Laptop received and processed');
      await fetchAll();
    } catch (err) {
      showToast(err.message, 'error');
      throw err;
    }
  };

  // ── Swap Asset ────────────────────────────────────────────────────────────
  const swapAsset = async (allocationDbId, oldAssetId, newAssetId, swapData) => {
    try {
      await apiFetch(`/allocations/${allocationDbId}/swap`, {
        method: 'PUT',
        body: JSON.stringify({
          new_asset_id:      newAssetId,
          issue_type:        swapData.issueType,
          issue_description: swapData.issueDesc,
          old_condition:     swapData.oldCondition,
          extra_ccs:         swapData.extra_ccs  || [],
          prepared_by:       swapData.preparedBy || '',
          issue_images:      swapData.issueImages || [],
        }),
      });
      showToast('Laptop swapped successfully');
      await fetchAll();
    } catch (err) {
      showToast(err.message, 'error');
      throw err;
    }
  };

  return (
    <AppContext.Provider value={{
      assets, allocations, repairs, scraps, stats,
      loading, toasts, users,
      addAsset, allocateAsset, receiveAsset, swapAsset,
      showToast, refetch: fetchAll,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
